import type { DuplicateExecutionData } from '../types';

export class ApiError extends Error {
  readonly code: number;
  readonly data: unknown;

  constructor(message: string, code: number, data: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.data = data;
  }
}

/** User-facing message when runPrompt returns 409 with isDuplicate. */
export const DUPLICATE_EXECUTION_MESSAGE =
  'این پرامپت امروز اجرا شده و دوباره در صف قرار نمی‌گیرد.';

type RtkQueryErrorLike = {
  status?: number | string;
  data?: {
    message?: string;
    msg?: string;
    data?: unknown;
  };
};

/** Normalize RTK Query unwrap / FetchBaseQuery errors into ApiError. */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;

  if (typeof error === 'object' && error != null && 'status' in error) {
    const e = error as RtkQueryErrorLike;
    const status = typeof e.status === 'number' ? e.status : 0;
    const payload = e.data;
    if (payload && typeof payload === 'object') {
      const message =
        payload.message || payload.msg || `Request failed (${status || 'unknown'})`;
      const data = 'data' in payload ? payload.data : payload;
      return new ApiError(message, status, data ?? null);
    }
    return new ApiError(`Request failed (${status || 'unknown'})`, status, null);
  }

  if (error instanceof Error) {
    return new ApiError(error.message, 0, null);
  }

  return new ApiError(String(error), 0, null);
}

function readDuplicateData(data: unknown): DuplicateExecutionData | null {
  if (typeof data !== 'object' || data == null) return null;
  const d = data as Partial<DuplicateExecutionData>;
  if (d.isDuplicate !== true || typeof d.existingRequestId !== 'string') return null;
  if (typeof d.executedAt !== 'string') return null;
  return d as DuplicateExecutionData;
}

export function isDuplicateExecutionError(
  error: unknown,
): error is ApiError & { data: DuplicateExecutionData } {
  if (!(error instanceof ApiError) || error.code !== 409) return false;
  return readDuplicateData(error.data) != null;
}

export function getDuplicateExecutionData(error: unknown): DuplicateExecutionData | null {
  const apiError = error instanceof ApiError ? error : toApiError(error);
  if (apiError.code !== 409) return null;
  return readDuplicateData(apiError.data);
}
